import { useState } from 'react';
import { Lock, Bell, Shield } from 'lucide-react';
import { Card, CardHeader, CardBody, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import api from '@/lib/api';

export function SettingsPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [chatNotifications, setChatNotifications] = useState(true);
  const [paymentNotifications, setPaymentNotifications] = useState(true);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setIsChangingPassword(true);

    try {
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message ?? 'Failed to change password';
      setPasswordError(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-gray-400 mt-1">Manage your account settings</p>
      </div>

      <form onSubmit={handleChangePassword}>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary-400" />
              Change Password
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {passwordError && (
              <div className="p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-error-400 text-sm">
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 bg-success-500/10 border border-success-500/30 rounded-lg text-success-400 text-sm">
                {passwordSuccess}
              </div>
            )}

            <Input
              label="Current Password"
              type="password"
              placeholder="Enter current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              leftIcon={<Lock className="w-4 h-4" />}
              required
            />
            <Input
              label="New Password"
              type="password"
              placeholder="At least 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              leftIcon={<Shield className="w-4 h-4" />}
              required
            />
            <Input
              label="Confirm New Password"
              type="password"
              placeholder="Repeat new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              leftIcon={<Shield className="w-4 h-4" />}
              required
            />
          </CardBody>
          <CardFooter>
            <div className="flex justify-end">
              <Button type="submit" isLoading={isChangingPassword}>
                Update Password
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary-400" />
            Notification Preferences
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          {[
            {
              label: 'Email Notifications',
              description: 'Receive email notifications for important updates',
              checked: emailNotifications,
              onChange: setEmailNotifications,
            },
            {
              label: 'Push Notifications',
              description: 'Receive browser push notifications',
              checked: pushNotifications,
              onChange: setPushNotifications,
            },
            {
              label: 'Chat Messages',
              description: 'Get notified about new chat messages',
              checked: chatNotifications,
              onChange: setChatNotifications,
            },
            {
              label: 'Payment Alerts',
              description: 'Get notified about payment events',
              checked: paymentNotifications,
              onChange: setPaymentNotifications,
            },
          ].map((pref) => (
            <div
              key={pref.label}
              className="flex items-center justify-between p-3 rounded-lg bg-dark-700/50"
            >
              <div>
                <p className="text-sm font-medium text-white">{pref.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{pref.description}</p>
              </div>
              <button
                type="button"
                onClick={() => pref.onChange(!pref.checked)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  pref.checked ? 'bg-primary-500' : 'bg-dark-500'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    pref.checked ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}
